<template>
  <v-container fluid class="pf" :style="themeVars">
    <page-skeleton v-if="loading" variant="profile" class="mt-4" />

    <template v-else>
      <!-- Card 86ak6hc2p: aluno cadastrado pelo administrativo entra sem passar
           pelo questionário inicial. As perguntas que ficaram sem resposta
           viram este convite — no perfil, não como barreira na entrada. -->
      <div v-if="profile.profileIncomplete" class="pf-complete">
        <v-icon size="22" color="#E8276F" class="pf-complete__icon">
          mdi-account-star-outline
        </v-icon>
        <div class="pf-complete__body">
          <div class="pf-complete__title">
            {{ $t("profile.completeTitle") }}
          </div>
          <p class="pf-complete__text">
            {{ $t("profile.completeText") }}
          </p>
        </div>
        <button
          type="button"
          class="pf-complete__btn"
          @click="goToCompleteInfo"
        >
          {{ $t("profile.completeNow") }}
        </button>
      </div>

      <v-form v-model="valid" ref="form">
        <!-- ============ CARTEIRA (identidade) ============ -->
        <div class="pf-card-id">
          <input
            type="file"
            ref="fileInput"
            id="fileInput"
            accept="image/*"
            class="pf-file"
            @change="onFilePick"
          />

          <button
            type="button"
            class="pf-avatar"
            @click="triggerFileInput"
            :aria-label="$t('profile.changePhoto')"
          >
            <v-img
              v-if="hasAvatar"
              cover
              :src="profile.avatar"
              class="pf-avatar__img"
            />
            <span v-else class="pf-avatar__initials">{{ initials }}</span>
            <span class="pf-avatar__cam">
              <v-icon size="18" color="#fff">mdi-camera-outline</v-icon>
            </span>
          </button>

          <div class="pf-id__body">
            <div class="pf-eyebrow">{{ $t("profile.yourCard") }}</div>
            <h1 class="pf-display pf-id__name">
              {{ profile.full_name || $t("profile.yourProfile") }}
            </h1>

            <div class="pf-id__meta">
              <span
                class="pf-stamp"
                :class="{ 'pf-stamp--alert': profile.pendingInfo }"
              >
                <v-icon size="15" class="mr-1">mdi-stairs</v-icon>
                {{ profile.englishLevel || $t("profile.placementPending") }}
              </span>
              <span v-if="modalityLabel" class="pf-chip">
                {{ modalityLabel }}
              </span>
              <span v-if="frequencyLabel" class="pf-chip">
                {{ frequencyLabel }}
              </span>
              <!-- Com professor, é o professor da turma atual; sem professor,
                   o tutor do plano solo (card 86ak6upm4). -->
              <span class="pf-chip pf-chip--soft" :title="mentorChip.title">
                <v-icon size="14" class="mr-1">mdi-account-tie</v-icon>
                {{ mentorChip.text }}
              </span>
            </div>
          </div>

          <div class="pf-id__actions">
            <v-btn rounded outlined class="pf-btn-ghost" @click="returnToHome">
              <v-icon left small>mdi-arrow-left</v-icon>{{ $t("profile.back") }}
            </v-btn>
            <v-btn
              rounded
              depressed
              class="pf-btn"
              :loading="loadingSave"
              @click="submit"
            >
              <v-icon left small>mdi-content-save-outline</v-icon
              >{{ $t("profile.update") }}
            </v-btn>
            <v-btn
              v-if="profile.pendingInfo"
              rounded
              depressed
              class="pf-btn-ember"
              @click="goToCompleteInfo"
            >
              {{ $t("profile.completeInfo") }}
            </v-btn>
          </div>
        </div>

        <!-- ============ RAIL DE ABAS ============ -->
        <div class="pf-rail" role="tablist">
          <button
            v-for="t in tabsList"
            :key="t.value"
            type="button"
            role="tab"
            class="pf-rail__tab"
            :class="{ 'pf-rail__tab--on': tab === t.value }"
            :aria-selected="tab === t.value"
            @click="tab = t.value"
          >
            <v-icon size="18" class="mr-1">{{ t.icon }}</v-icon>
            {{ t.label }}
          </button>
        </div>

        <v-tabs-items v-model="tab" class="pf-tabs-items">
          <!-- ---------- Dados gerais ---------- -->
          <v-tab-item value="tab-1">
            <div class="pf-card pf-card--pad">
              <div class="pf-section-title">{{ $t("profile.personal") }}</div>
              <v-row dense class="mt-2">
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    v-model="profile.full_name"
                    :label="$t('profile.name')"
                    :currentValue="profile.full_name"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    v-model="profile.phone"
                    :outlined="true"
                    :label="$t('profile.phone')"
                    :currentValue="profile.phone"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.email')"
                    v-model="profile.email"
                    :currentValue="profile.email"
                    :customRule="rules.email"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" md="4">
                  <custom-masked-input
                    :outlined="true"
                    :label="$t('profile.cpf')"
                    :currentValue="profile.document"
                    :customRule="rules.document"
                    v-model="profile.document"
                    :mask="'###.###.###-##'"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-masked-input
                    :outlined="true"
                    :label="$t('profile.birthDate')"
                    v-model="profile.birthDate"
                    :currentValue="profile.birthDate"
                    :mask="'##/##/####'"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    type="password"
                    :label="$t('profile.password')"
                    :hint="$t('profile.passwordHint')"
                    :customRule="rules.optionalPwd"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    :label="$t('profile.bio')"
                    :placeholder="$t('profile.bioPlaceholder')"
                    :outlined="true"
                    rows="4"
                    v-model="profile.bio"
                  />
                </v-col>
              </v-row>

              <template v-if="profile.underAge">
                <div class="pf-subhead">
                  <span class="pf-eyebrow pf-eyebrow--ember">{{
                    $t("profile.guardian")
                  }}</span>
                </div>
                <div class="pf-inset">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <custom-input
                        v-model="profile.legalGuardian.name"
                        :currentValue="profile.legalGuardian.name"
                        :outlined="true"
                        :label="$t('profile.fullName')"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <custom-input
                        :outlined="true"
                        :label="$t('profile.email')"
                        :customRule="rules.email"
                        :currentValue="profile.legalGuardian.email"
                        v-model="profile.legalGuardian.email"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <custom-masked-input
                        :label="$t('profile.guardianPhone')"
                        placeholder="(11)99999-9999"
                        :hint="$t('profile.phoneHint')"
                        :mask="'(##)#####-####'"
                        :outlined="true"
                        v-model="profile.legalGuardian.phone"
                        :currentValue="profile.legalGuardian.phone"
                      />
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <custom-masked-input
                        :outlined="true"
                        :label="$t('profile.cpf')"
                        :mask="'###.###.###-##'"
                        :customRule="rules.document"
                        :currentValue="profile.legalGuardian.document"
                        v-model="profile.legalGuardian.document"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <custom-masked-input
                        :outlined="true"
                        :label="$t('profile.birthDate')"
                        :mask="'##/##/####'"
                        :customRule="rules.validDate"
                        :currentValue="profile.legalGuardian.birthDate"
                        v-model="profile.legalGuardian.birthDate"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="pf-field-label">
                        {{ $t("profile.kinship") }}
                      </div>
                      <v-select
                        outlined
                        rounded
                        background-color="var(--dk-wash, #F1F3F5)"
                        :items="parentGrades"
                        :item-value="`value`"
                        :item-text="`text`"
                        v-model="profile.legalGuardian.kinship"
                      />
                    </v-col>
                  </v-row>
                </div>
              </template>
            </div>

            <div class="pf-card pf-card--pad mt-4">
              <div class="pf-section-title">{{ $t("profile.address") }}</div>
              <v-row dense class="mt-2">
                <v-col cols="12" md="4">
                  <debounced-input
                    :outlined="true"
                    ref="zipCodeInput"
                    :label="$t('profile.zip')"
                    placeholder="12345678"
                    :hint="$t('profile.onlyNumbers')"
                    v-model="profile.address.zipCode"
                    :currentValue="profile.address.zipCode"
                    @search="getAddressByZipCode"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    ref="streetInput"
                    :outlined="true"
                    :label="$t('profile.street')"
                    :currentValue="profile.address.street"
                    v-model="profile.address.street"
                    :placeholder="
                      loadingAddress
                        ? $t('profile.loading')
                        : $t('profile.streetPlaceholder')
                    "
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.number')"
                    :currentValue="profile.address.number"
                    v-model="profile.address.number"
                  />
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.complement')"
                    :currentValue="profile.address.complement"
                    v-model="profile.address.complement"
                    :required="false"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    ref="neighboorhoodInput"
                    :label="$t('profile.neighborhood')"
                    :currentValue="profile.address.neighboorhood"
                    v-model="profile.address.neighboorhood"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.city')"
                    ref="cityInput"
                    :currentValue="profile.address.city"
                    v-model="profile.address.city"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <custom-input
                    :outlined="true"
                    :label="$t('profile.state')"
                    ref="provinceInput"
                    :currentValue="profile.address.province"
                    v-model="profile.address.province"
                  />
                </v-col>
              </v-row>
            </div>
          </v-tab-item>

          <!-- ---------- Disponibilidade ---------- -->
          <v-tab-item value="tab-2">
            <div class="pf-card pf-card--pad">
              <!-- Explica por que o aluno foi trazido para cá em vez de deixá-lo
                   se perguntando por que a aba mudou sozinha. -->
              <div
                v-if="availabilityPrompt && !hasAvailability"
                class="pf-gate"
              >
                <v-icon size="20" color="#B5421E" class="pf-gate__icon"
                  >mdi-information-outline</v-icon
                >
                <div>
                  <div class="pf-gate__title">
                    {{ $t("profile.gateTitle") }}
                  </div>
                  <i18n path="profile.gateText" tag="p" class="pf-gate__text">
                    <template #link>
                      <button
                        type="button"
                        class="pf-gate__link"
                        @click="tab = 'tab-3'"
                      >
                        {{ $t("profile.studyPlanLink") }}
                      </button>
                    </template>
                  </i18n>
                </div>
              </div>

              <div class="pf-section-title">{{ $t("profile.yourTimes") }}</div>
              <div class="pf-section-sub">
                {{ $t("profile.yourTimesSub") }}
              </div>

              <div
                v-for="(available, index) in profile.aviability"
                :key="index"
                class="pf-slot"
              >
                <div class="pf-slot__field">
                  <div class="pf-field-label">{{ $t("profile.weekDay") }}</div>
                  <v-select
                    v-model="available.weekDay"
                    :items="weekDaysItems"
                    :item-text="`text`"
                    :item-value="`value`"
                    outlined
                    hide-details
                    :disabled="!available.editable"
                  />
                </div>
                <div class="pf-slot__field">
                  <div class="pf-field-label">{{ $t("profile.start") }}</div>
                  <v-select
                    v-model="available.from"
                    outlined
                    hide-details
                    :items="hoursItemsHalf"
                    :disabled="!available.editable"
                    @change="updateEnd(available)"
                  />
                </div>
                <div class="pf-slot__field">
                  <div class="pf-field-label">{{ $t("profile.end") }}</div>
                  <v-select
                    v-model="available.to"
                    outlined
                    hide-details
                    :items="hoursItemsHalf"
                    :disabled="!available.editable"
                  />
                </div>
                <v-btn
                  icon
                  class="pf-slot__del"
                  @click="removerDisponibilidade(available.id, index)"
                  :disabled="!available.editable"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>

              <button type="button" class="pf-add" @click="inserirHorario">
                <v-icon size="18" class="mr-1">mdi-plus</v-icon>
                {{ $t("profile.addTime") }}
              </button>
            </div>
          </v-tab-item>

          <!-- ---------- Plano de estudos ---------- -->
          <v-tab-item value="tab-3">
            <!-- Aluno sem professor: tutor designado e plano solo. -->
            <div v-if="+profile.preference === 1" class="pf-card pf-card--pad">
              <div class="pf-section-title pf-section-title--icon">
                <v-icon size="20" class="mr-2">mdi-account-tie</v-icon>
                {{ $t("profile.yourTutor") }}
              </div>
              <div v-if="profile.tutor">
                <p class="pf-strong mb-1">{{ profile.tutor.name }}</p>
                <p class="pf-section-sub mb-0">
                  {{ $t("profile.tutorText") }}
                </p>
              </div>
              <p v-else class="pf-section-sub mb-0">
                {{ $t("profile.tutorSoon") }}
              </p>
              <i18n
                v-if="profile.soloMonthlyValue"
                path="profile.soloPlan"
                tag="p"
                class="mt-3 mb-0"
              >
                <template #value>
                  <strong>{{
                    formatCurrency(profile.soloMonthlyValue)
                  }}</strong>
                </template>
              </i18n>
            </div>

            <!-- Card 17trt6jdacg: modalidade e frequência escolhidas no
                 cadastro podiam não ser mais alteradas. O aluno solo responde
                 as duas no match, quando decide ter professor. -->
            <div v-if="!isSoloStudent" class="pf-card pf-card--pad">
              <div class="pf-section-title pf-section-title--icon">
                <v-icon size="20" class="mr-2">mdi-tune-variant</v-icon>
                {{ $t("profile.preference") }}
              </div>
              <p class="pf-section-sub">
                {{ $t("profile.preferenceSub") }}
              </p>

              <v-row dense class="mt-2">
                <v-col cols="12" sm="6">
                  <div class="pf-field-label">{{ $t("profile.modality") }}</div>
                  <v-select
                    v-model="prefModality"
                    :items="preferenceModalityOptions"
                    item-text="text"
                    item-value="value"
                    :placeholder="$t('profile.select')"
                    color="#E8276F"
                    item-color="#E8276F"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="pf-field-label">
                    {{ $t("profile.frequency") }}
                  </div>
                  <v-select
                    v-model="prefFrequency"
                    :items="preferenceFrequencyOptions"
                    item-text="text"
                    item-value="value"
                    :placeholder="$t('profile.select')"
                    color="#E8276F"
                    item-color="#E8276F"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-end mt-3">
                <v-btn
                  rounded
                  depressed
                  dark
                  color="#E8276F"
                  :loading="savingPreferences"
                  :disabled="!preferencesChanged"
                  @click="saveStudyPreferences"
                >
                  {{ $t("profile.savePreference") }}
                </v-btn>
              </div>
            </div>

            <!-- Possibilidades de match. O aluno solo já tem o bloco do tutor
                 acima; para ele, trocar de modalidade é voltar para turma. -->
            <div class="pf-card pf-card--pad mt-4">
              <MatchExplorer
                ref="matchExplorer"
                :linked="linkedToTeacher"
                @requested="loadGroups"
                @changed="onStudyModeChanged"
                @needs-availability="goToAvailability"
              />
            </div>
          </v-tab-item>

          <!-- ---------- Conversação ---------- -->
          <v-tab-item value="tab-5">
            <!-- Card 86aknajnn: o aluno Autonomy acompanha a conversação aqui,
                 no perfil, e ficava com o extrato antigo -- um número só para
                 conversação e monitoria, e a lista inteira sem filtro. O
                 componente é o mesmo da página de turmas de quem tem professor,
                 para as duas telas não voltarem a divergir. -->
            <conversation-attendance />
          </v-tab-item>

          <!-- ---------- Financeiro ---------- -->
          <v-tab-item value="tab-4">
            <div class="pf-card pf-card--pad">
              <div class="pf-section-title">
                {{ $t("profile.paymentMethod") }}
              </div>
              <p class="pf-section-sub">
                {{ $t("profile.paymentSub") }}
              </p>
              <payment-method-picker
                class="mt-4"
                :value="paymentPreferred"
                :options="paymentOptions"
                :card="profile.card || {}"
                :busy="savingPaymentMethod"
                @select="choosePaymentMethod"
              />
            </div>

            <!-- O cartão só importa para quem paga no cartão; quem escolheu
                 Pix ou boleto não precisa ver nem manter um. -->
            <div
              v-if="paymentPreferred === 'credit_card'"
              class="pf-card pf-card--pad mt-4"
            >
              <div class="pf-section-title">
                {{ $t("profile.yourCardTitle") }}
              </div>
              <v-row dense align="stretch" class="mt-2">
                <v-col
                  cols="12"
                  md="6"
                  v-for="(card, index) in cards"
                  :key="index"
                >
                  <Card :data="card" />
                </v-col>
                <v-col cols="12" md="6">
                  <button
                    type="button"
                    class="pf-addcard"
                    @click="openCheckout"
                  >
                    <v-icon size="30" color="#E8276F"
                      >mdi-credit-card-plus-outline</v-icon
                    >
                    <span class="mt-2">{{
                      cards.length
                        ? $t("profile.replaceCard")
                        : $t("profile.addCard")
                    }}</span>
                  </button>
                </v-col>
              </v-row>
            </div>
            <!-- Fora do v-if: o cartão também é cadastrado a partir do botão
                 "Cartão de crédito" do seletor, quando o aluno está no Pix. -->
            <CheckoutDialog ref="checkout" @saved="onCardSaved" />
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </template>
  </v-container>
</template>
<script>
import { cpf } from "cpf-cnpj-validator";
import addressAPI from "@/shared/addressApi";
import Card from "@/components/checkout/Card.vue";
import customerApi from "@/shared/pitchCustomerApi";
import Colors from "@/styles/app.module.scss";
import { isBefore, isValid, parse, differenceInYears } from "date-fns";
import CheckoutDialog from "@/components/checkout/CheckoutDialog.vue";
import MatchExplorer from "@/components/connect/customer/MatchExplorer.vue";
import ConversationAttendance from "@/components/connect/customer/ConversationAttendance.vue";
import PaymentMethodPicker from "@/components/connect/customer/PaymentMethodPicker.vue";
import {
  PAYMENT_METHOD_MISSING,
  paymentMethodLabel,
} from "@/shared/paymentMethods";
import {
  GROUP_FORMAT_OPTIONS,
  GROUP_FREQUENCY_OPTIONS,
  WEEK_DAY_ITEMS,
  displayGroupFormat,
  displayGroupFrequency,
} from "@/shared/classPresentation";
import { formatBRL } from "@/plugins/i18n";
export default {
  name: "CustomerProfile",
  components: {
    Card,
    CheckoutDialog,
    MatchExplorer,
    ConversationAttendance,
    PaymentMethodPicker,
  },
  mixins: [],
  props: {},
  computed: {
    // Derivado do perfil, não guardado à parte: antes só o loadData (chamado
    // depois de salvar um cartão) preenchia a lista, e o mounted não. O cartão
    // aparecia logo após o cadastro e sumia ao recarregar a página.
    cards() {
      return this.buildSavedCards(this.profile.card, this.profile.full_name);
    },
    paymentPreferred() {
      return this.profile.paymentMethod?.preferred || "credit_card";
    },
    paymentOptions() {
      return this.profile.paymentMethod?.options || [];
    },
    themeVars() {
      return {
        "--customer-primary": Colors["primary-500"],
        "--customer-primary-soft": Colors["primary-100"],
      };
    },
    // Card 86ak6upm4: os chips do cabecalho eram texto fixo ("Dupla",
    // "2x / semana") e apareciam para todo aluno. Agora vem do perfil; o
    // aluno solo (preference 1) nao escolhe modalidade nem frequencia e grava
    // 0 nas duas, entao nao ha o que mostrar.
    isSoloStudent() {
      // A API devolve preference (INT8) como string: "1", nao 1.
      return +this.profile.preference === 1;
    },
    preferencesChanged() {
      const prefs = this.profile.studyPreferences || {};
      if (!this.prefModality || !this.prefFrequency) return false;
      return (
        +this.prefModality !== +(prefs.formatGoal || 0) ||
        +this.prefFrequency !== +(prefs.perWeek || 0)
      );
    },
    // Retorno de 22/09: quem conduz o aluno hoje. Com turma, o professor
    // dela; sem turma, o tutor do plano solo.
    mentorChip() {
      if (this.profile.teacher) {
        return {
          text: this.profile.teacher.name,
          title: this.$t("profile.yourTeacher"),
        };
      }

      if (this.profile.tutor) {
        return {
          text: this.profile.tutor.name,
          title: this.$t("profile.yourTutorTitle"),
        };
      }

      return this.isSoloStudent
        ? {
            text: this.$t("profile.noTutor"),
            title: this.$t("profile.noTutorTitle"),
          }
        : {
            text: this.$t("profile.noTeacher"),
            title: this.$t("profile.noTeacherTitle"),
          };
    },
    // Os chips de modalidade e frequência seguem a turma atual: a API manda a
    // da turma ativa e só cai no cadastro enquanto não houver turma.
    //
    // No cadastro do aluno o 0 nao e escolha ("Sozinho" / "Todos os dias"):
    // e o valor neutro de quem nao respondeu, por isso so 1+ vira chip.
    modalityLabel() {
      if (this.isSoloStudent || !(+this.profile.formatGoal > 0)) return null;
      return displayGroupFormat(this.profile.formatGoal);
    },
    frequencyLabel() {
      if (this.isSoloStudent || !(+this.profile.perWeek > 0)) return null;
      return displayGroupFrequency(this.profile.perWeek);
    },
    initials() {
      return (
        this.getInitials(this.profile.full_name) ||
        localStorage.getItem("initials") ||
        ""
      );
    },
    // Decide se o aviso de desvinculo aparece nas opcoes de match.
    // Card 86ajhjzyc: com professor, o extrato de conversação vive na
    // página de turmas. Sem professor não há página de turmas para o aluno,
    // então a aba continua sendo o lugar dele.
    allTabs() {
      return [
        {
          value: "tab-1",
          label: this.$t("profile.tabGeneral"),
          icon: "mdi-account-outline",
        },
        {
          value: "tab-2",
          label: this.$t("profile.tabAvailability"),
          icon: "mdi-calendar-clock-outline",
        },
        {
          value: "tab-3",
          label: this.$t("profile.tabPlan"),
          icon: "mdi-stairs",
        },
        {
          value: "tab-5",
          label: this.$t("profile.tabConversation"),
          icon: "mdi-forum-outline",
        },
        {
          value: "tab-4",
          label: this.$t("profile.tabFinance"),
          icon: "mdi-wallet-outline",
        },
      ];
    },
    parentGrades() {
      return [1, 2, 3, 4, 5, 6].map((value) => ({
        text: this.$t(`profile.kin.${value}`),
        value,
      }));
    },
    tabsList() {
      return this.allTabs.filter(
        (item) => item.value !== "tab-5" || !this.conversationLivesInClasses
      );
    },
    hasActiveGroup() {
      return this.groupsCount > 0;
    },
    // Retorno de 08/09: a regra é "com professor", não "com turma". O aluno
    // Autonomy também recebe uma turma — a página de turma dele é montada pela
    // API (count 1) — e a contagem sozinha escondia a aba justamente de quem
    // deveria continuar com ela no perfil.
    //
    // Card 86akj67e4: a mesma armadilha aparecia no MatchExplorer, que dizia
    // "Você já está em uma turma" para o aluno solo. A regra ganhou nome aqui
    // para não ser reescrita a cada tela que precisar dela — foi assim que o
    // bug nasceu.
    linkedToTeacher() {
      return this.hasActiveGroup && !this.isSoloStudent;
    },
    conversationLivesInClasses() {
      return this.linkedToTeacher;
    },
    // O aviso de "cadastre um horario" se apaga sozinho assim que existe uma
    // janela na lista: ele e orientacao, nao um erro para ficar preso na tela.
    hasAvailability() {
      return (this.profile.aviability || []).length > 0;
    },
  },
  data() {
    return {
      loading: true,
      loadingSave: false,
      loadingAddress: false,
      groupsCount: 0,
      valid: false,
      tab: "tab-1",
      availabilityPrompt: false,
      // Preferência de estudo (card 17trt6jdacg). Mesmos valores do match.
      prefModality: null,
      prefFrequency: null,
      savingPreferences: false,
      preferenceFrequencyOptions: GROUP_FREQUENCY_OPTIONS.filter((option) =>
        [1, 2, 3, 4].includes(option.value)
      ),
      preferenceModalityOptions: GROUP_FORMAT_OPTIONS,
      hasAvatar: false,
      avatar: null,
      attrs: {
        class: "mb-6",
        boilerplate: true,
        elevation: 2,
      },
      profile: {
        name: "",
        phone: "",
        email: "",
        document: "",
        birthDate: "",
        password: "",
        preference: null,
        perWeek: null,
        formatGoal: null,
        teacher: null,
        tutor: null,
        soloMonthlyValue: null,
        details: {
          skills: {},
          mainContacts: [],
          currentAreas: [],
        },
        address: {
          street: "",
          number: "",
          complement: "",
          neighboorhood: "",
          city: "",
          province: "",
          zipCode: "",
        },
        aviability: [],
      },
      rules: {
        validDate: [
          (v) => this.validDate(v) || this.$t("profile.invalidBirth"),
        ],
        document: [
          (v) => this.validateDocNumber(v) || this.$t("checkout.invalidDoc"),
        ],
        email: [
          (v) => !!v || this.$t("common.requiredFieldDot"),
          (v) => /.+@.+\..+/.test(v) || this.$t("common.invalidEmail"),
        ],
        optionalPwd: [
          (v) => this.testPWD(v) || this.$t("profile.passwordRule"),
        ],
        password: [
          (v) => !!v || this.$t("common.requiredFieldDot"),
          (v) =>
            /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%*#¢+§])[A-Za-z\d@$!%*#?$§-]{8,}$/.test(
              v
            ) || this.$t("profile.passwordRule"),
        ],
        required: [(v) => !!v || this.$t("completeInfo.selectOption")],
      },
      savingPaymentMethod: null,
      // Forma que o aluno pediu mas que dependia de cadastrar o cartão antes;
      // é gravada quando o CheckoutDialog confirma o cartão.
      pendingPaymentMethod: null,
      englishLevels: [
        {
          text: "Beginner 1",
          value: "beginner_1",
        },
        {
          text: "Beginner 2",
          value: "beginner_2",
        },
        {
          text: "Essential 1",
          value: "essential_1",
        },
        {
          text: "Essential 2",
          value: "essential_2",
        },
        {
          text: "Pre-Intermediate 1",
          value: "pre_intermediate_1",
        },
        {
          text: "Pre-Intermediate 2",
          value: "pre_intermediate_2",
        },
        {
          text: "Intermediate 1",
          value: "intermediate_1",
        },
        {
          text: "Intermediate 2",
          value: "intermediate_2",
        },
        {
          text: "Upper 1",
          value: "upper_1",
        },
        {
          text: "Upper 2",
          value: "upper_2",
        },
        {
          text: "Advanced 1",
          value: "Advanced_1",
        },
        {
          text: "Advanced 2",
          value: "advanced_2",
        },
      ],
      types: [
        {
          text: "Conversation",
          value: "ALONE",
        },
        {
          text: "Dupla",
          value: "DUO",
        },
        {
          text: "Trio",
          value: "TRIO",
        },
        {
          text: "Quarteto",
          value: "SQUAD",
        },
      ],
      times: [
        {
          text: "1:00",
          value: "1h",
        },
        {
          text: "1:30",
          value: "1:30h",
        },
      ],
      weekDaysItems: WEEK_DAY_ITEMS,
      learningWays: [
        "Música",
        "Filmes e Séries",
        "Livros",
        "Estudos",
        "Trabalho",
        "Outras Formas",
        "Amigos",
        "Redes Sociais",
      ],
      currentAreas: [
        "Estudante",
        "Educação",
        "Comércio",
        "Tecnologia",
        "Saúde",
        "Outros",
      ],
      hoursItemsHalf: [
        "06:00",
        "06:30",
        "07:00",
        "07:30",
        "08:00",
        "08:30",
        "09:00",
        "09:30",
        "10:00",
        "10:30",
        "11:00",
        "11:30",
        "12:00",
        "12:30",
        "13:00",
        "13:30",
        "14:00",
        "14:30",
        "15:00",
        "15:30",
        "16:00",
        "16:30",
        "17:00",
        "17:30",
        "18:00",
        "18:30",
        "19:00",
        "19:30",
        "20:00",
        "20:30",
        "21:00",
        "21:30",
        "22:00",
      ],
    };
  },
  watch: {
    // Some a aba enquanto ela está aberta (o aluno acabou de entrar numa
    // turma): sem isto o painel ficaria em branco, sem aba correspondente.
    conversationLivesInClasses(moved) {
      if (moved && this.tab === "tab-5") {
        this.tab = "tab-1";
      }
    },
    "profile.birthDate": {
      handler: function (value) {
        if (value) {
          try {
            const birthDate = parse(value, "dd/MM/yyyy", new Date());

            const difference = differenceInYears(new Date(), birthDate);
            if (difference < 18) {
              this.profile.underAge = true;
              if (!this.profile.legalGuardian) {
                this.$set(this.profile, "legalGuardian", {
                  name: "",
                  kinship: null,
                  document: "",
                  email: "",
                  birthDate: "",
                  phone: "",
                });
              }
            } else {
              this.profile.underAge = false;
            }
          } catch (e) {
            return e;
          }
        }
      },
      deep: true,
    },
    avatar: function (value) {
      if (value) {
        this.profile.avatar = URL.createObjectURL(value);
        this.hasAvatar = true;
      }
    },
    // As abas ficam montadas, entao o MatchExplorer nao recarrega sozinho ao
    // voltar. Sem isto, o aluno cadastra os horarios e reencontra a mesma lista
    // vazia de antes -- exatamente o beco sem saida que o aviso tenta evitar.
    tab(value) {
      if (value !== "tab-3") return;
      this.$refs.matchExplorer?.reload();
    },
  },
  mounted() {
    customerApi.showProfile().then((res) => {
      this.profile = Object.assign({}, res.data);
      this.syncStudyPreferences();
      if (!this.profile.details) {
        this.profile.details = {
          skills: {},
          mainContacts: [],
          currentAreas: [],
        };
      }
      this.loading = false;
    });
    this.loadGroups();
    this.applyTabFromRoute();
  },
  methods: {
    // Permite cair direto numa aba por link (ex.: a tela de turmas manda o aluno
    // Autonomy para a disponibilidade). Aba desconhecida cai na primeira.
    applyTabFromRoute() {
      const requested = this.$route.query.aba;
      if (!requested) return;

      const exists = this.tabsList.some((item) => item.value === requested);
      if (exists) this.tab = requested;
    },
    onFilePick(event) {
      const file = event?.target?.files?.[0];
      if (file) this.avatar = file;
    },
    // Saber se o aluno tem turma decide o aviso de desvinculo nas opcoes de
    // match. Falha silenciosa: sem a contagem, o aviso so nao aparece.
    loadGroups() {
      customerApi
        .groups()
        .then((res) => {
          this.groupsCount = Number(res.data?.count) || 0;
        })
        .catch(() => {
          this.groupsCount = 0;
        });
    },
    // O match nao tem com o que cruzar sem horario cadastrado; o MatchExplorer
    // avisa e a gente move o aluno para onde o bloqueio se resolve.
    goToAvailability() {
      this.availabilityPrompt = true;
      this.tab = "tab-2";
    },
    onStudyModeChanged() {
      this.groupsCount = 0;
      customerApi.showProfile().then((res) => {
        this.profile = Object.assign({}, this.profile, res.data);
        this.syncStudyPreferences();
      });
    },
    syncStudyPreferences() {
      const prefs = this.profile.studyPreferences || {};
      this.prefModality = prefs.formatGoal ? +prefs.formatGoal : null;
      this.prefFrequency = prefs.perWeek ? +prefs.perWeek : null;
    },
    async saveStudyPreferences() {
      if (!this.preferencesChanged || this.savingPreferences) return;

      this.savingPreferences = true;

      try {
        const res = await customerApi.saveStudyPreferences({
          perWeek: this.prefFrequency,
          formatGoal: this.prefModality,
        });
        const studyPreferences = res.data?.data || {
          perWeek: this.prefFrequency,
          formatGoal: this.prefModality,
        };
        // Sem turma, os chips do cartão mostram a preferência (a API cai
        // nela quando não há turma atual): acompanham o que acabou de salvar.
        const chips = this.hasActiveGroup
          ? {}
          : {
              perWeek: studyPreferences.perWeek,
              formatGoal: studyPreferences.formatGoal,
            };
        this.profile = Object.assign({}, this.profile, chips, {
          studyPreferences,
        });
        this.$root.$emit("display-alert", {
          text: this.$t("profile.preferenceSaved"),
          type: 0,
        });
        // O match usa a preferência: as opções da aba mudam com ela.
        this.$refs.matchExplorer?.reload();
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: error?.response?.data?.message || this.$t("match.saveFailed"),
          type: 1,
        });
      } finally {
        this.savingPreferences = false;
      }
    },
    formatCurrency(value) {
      return formatBRL(value);
    },
    getInitials(name) {
      const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (parts.length === 0) return "";
      if (parts.length === 1) return parts[0].substring(0, 1).toUpperCase();

      return `${parts[0].substring(0, 1)} ${parts[1]
        .substring(0, 1)
        .toUpperCase()}`;
    },
    isValidAvatarUrl(value) {
      if (!value || typeof value !== "string") return false;

      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    buildSavedCards(card, fullName) {
      if (!card || !card.ready) return [];
      return [
        {
          current: true,
          name: card.brand || this.$t("profile.card"),
          holder: card.holderName || fullName || "",
          maskedNumber: `**** **** **** ${card.last4 || "0000"}`,
          expMonth: "",
          expYear: "",
        },
      ];
    },
    loadData() {
      customerApi.showProfile().then((res) => {
        this.profile = Object.assign({}, res.data);
        if (!this.profile.details) {
          this.profile.details = {
            skills: {},
            mainContacts: [],
            currentAreas: [],
          };
        }
        this.loading = false;
        this.hasAvatar = this.isValidAvatarUrl(res.data.avatar);
        this.$store.dispatch("setAvatar", res.data.avatar || null);
        this.$store.dispatch("setUser", {
          ...(this.$store.getters.user || {}),
          name: res.data.full_name || this.profile.full_name || "",
          full_name: res.data.full_name || this.profile.full_name || "",
          avatar: res.data.avatar || null,
        });
      });
    },
    testPWD(value) {
      if (!value) return true;
      return /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%*#¢+§])[A-Za-z\d@$!%*#?$§-]{8,}$/.test(
        value
      );
    },
    openCheckout() {
      this.$refs.checkout.open();
    },
    choosePaymentMethod(method) {
      if (method === this.paymentPreferred || this.savingPaymentMethod) return;

      const option = this.paymentOptions.find((item) => item.method === method);

      if (option && !option.available) {
        // Sem cartão, escolher "Cartão de crédito" é cadastrar um: a escolha
        // é gravada logo depois que o cartão for salvo.
        if (option.reason === "card") {
          this.pendingPaymentMethod = method;
          this.openCheckout();
          return;
        }

        // CPF e endereço ficam em Dados gerais: leva o aluno até lá.
        this.$root.$emit("display-alert", {
          text: this.$t("profile.missingFor", {
            missing: PAYMENT_METHOD_MISSING[option.reason],
            method: paymentMethodLabel(method),
          }),
          type: 1,
        });
        this.tab = "tab-1";
        return;
      }

      this.savePaymentMethod(method);
    },
    async savePaymentMethod(method) {
      this.savingPaymentMethod = method;

      try {
        const res = await customerApi.setPaymentMethod(method);
        this.profile = {
          ...this.profile,
          paymentMethod: {
            preferred: res.data.preferredPaymentMethod,
            options: res.data.options || this.paymentOptions,
          },
        };
        this.$root.$emit("display-alert", {
          text: this.$t("profile.methodChanged", {
            method: paymentMethodLabel(method),
          }),
          type: 0,
        });
      } catch (error) {
        this.$root.$emit("display-alert", {
          text:
            error?.response?.data?.message || this.$t("profile.methodFailed"),
          type: 1,
        });
      } finally {
        this.savingPaymentMethod = null;
      }
    },
    async onCardSaved() {
      const pending = this.pendingPaymentMethod;
      this.pendingPaymentMethod = null;

      // Se o aluno fechou o checkout antes e voltou depois, a forma pendente
      // pode já ser a atual -- aí não há o que gravar.
      if (pending && pending !== this.paymentPreferred) {
        await this.savePaymentMethod(pending);
      }

      this.loadData();
    },
    validateDocNumber(doc) {
      return cpf.isValid(doc);
    },
    validDate(d) {
      const inputedDate = parse(d, "dd/MM/yyyy", new Date());
      return isValid(inputedDate) && isBefore(inputedDate, new Date());
    },
    // Leva ao questionário do cadastro. Serve aos dois estados: quem nunca
    // teve perfil (`pendingInfo`) e quem entrou pelo administrativo e ainda
    // não respondeu as perguntas iniciais (`profileIncomplete`, card
    // 86ak6hc2p) — é o que troca o "Beginner 1 por padrão" pelo nível
    // calculado.
    goToCompleteInfo() {
      this.$router.push({ name: "CompletarInformacoes" });
    },
    returnToHome() {
      this.$router.push({
        path: "cockpit",
      });
    },
    async submit() {
      if (this.$refs.form.validate()) {
        this.loadingSave = true;
        if (this.avatar) {
          const formData = new FormData();
          formData.append("avatar", this.avatar);
          await customerApi.uploadFiles(formData);
        }

        const payload = {
          ...this.profile,
        };

        delete payload.avatar;

        if (payload.pendingInfo) {
          payload.details = {
            ...payload.details,
            ...payload.details.skills,
          };
        }

        customerApi
          .updateProfile(payload)
          .then(async () => {
            this.$root.$emit("display-alert", {
              text: this.$t("profile.updated"),
              type: 0,
            });
            await this.loadData();
          })
          .catch((err) => {
            const raw =
              err?.response?.data?.message || err?.data?.message || "";
            const message = Array.isArray(raw) ? raw.join(" ") : raw;
            this.$root.$emit("display-alert", {
              text: message || this.$t("profile.updateFailed"),
              type: 1,
            });
          })
          .finally(() => {
            this.loadingSave = false;
          });
      } else {
        this.$root.$emit("display-alert", {
          text: this.$t("profile.fillRequired"),
          type: 1,
        });
      }
    },
    getAddressByZipCode() {
      if (this.profile.address.zipCode) {
        addressAPI
          .getAddresByZipCode(this.profile.address.zipCode)
          .then(async (res) => {
            this.$refs.streetInput.handleInput(res.data.address);
            this.$refs.neighboorhoodInput.handleInput(res.data.district);
            this.$refs.cityInput.handleInput(res.data.city);
            this.$refs.provinceInput.handleInput(res.data.state);
            this.readOnly = false;
            this.loadingAddress = false;
          })
          .catch(() => {
            this.loadingAddress = false;
            this.readOnly = false;
          });
      }
    },
    triggerFileInput() {
      document.getElementById("fileInput").click();
    },

    updateEnd(av) {
      if (!av.from) return;

      const [h, m] = av.from.split(":").map(Number);
      const d = new Date(2000, 0, 1, h, m);
      // duração padrão de aula: 60 minutos
      d.setMinutes(d.getMinutes() + 60);

      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");

      const result = `${hh}:${mm}`;
      // só sobrescreve quando o horário calculado existe na lista; caso
      // contrário preserva o valor que o usuário tenha escolhido manualmente.
      if (this.hoursItemsHalf.includes(result)) {
        av.to = result;
      }
    },

    async removerDisponibilidade(id, index) {
      if (id) {
        await customerApi
          .removerDisponibilidade(id)
          .then(() => {
            this.profile.aviability.splice(index, 1);
            this.$root.$emit("display-alert", {
              text: this.$t("profile.availabilityUpdated"),
              type: 0,
            });
          })
          .catch(() => {
            this.$root.$emit("display-alert", {
              text: this.$t("profile.availabilityFailed"),
              type: 1,
            });
          });
      } else {
        this.profile.aviability.splice(index, 1);
      }
    },

    inserirHorario() {
      if (!Array.isArray(this.profile.aviability)) {
        this.$set(this.profile, "aviability", []);
      }
      this.profile.aviability.push({
        weekDay: null,
        from: null,
        to: null,
        editable: true,
      });
    },
  },
};
</script>
<style scoped>
.pf {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --accent: var(--dk-pink, #e8276f);
  --accent-soft: rgba(232, 39, 111, 0.1);
  --teal: var(--dk-teal, #00a379);
  --ember: var(--dk-ember, #ff5d2a);
  width: 100%;
  padding: 8px 4px 48px;
  color: var(--ink);
}

.pf-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

.pf-file {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.pf-eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--accent);
}
.pf-eyebrow--ember {
  color: var(--ember);
}

/* ---------- Carteira ---------- */
.pf-card-id {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 24px;
  align-items: center;
  margin-top: 12px;
  padding: 24px 28px;
  border-radius: 24px;
  background: radial-gradient(
      120% 150% at 0% 0%,
      var(--accent-soft),
      transparent 55%
    ),
    var(--dk-surface, #fff);
  border: 1px solid var(--line);
  box-shadow: 0 22px 48px -38px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
}
.pf-avatar {
  position: relative;
  width: 116px;
  height: 116px;
  border-radius: 50%;
  padding: 0;
  cursor: pointer;
  background: var(--accent);
  border: 3px solid var(--dk-surface, #fff);
  box-shadow: 0 0 0 3px var(--accent-soft),
    0 12px 26px -12px rgba(232, 39, 111, 0.6);
  overflow: hidden;
}
.pf-avatar__img {
  width: 100%;
  height: 100%;
}
.pf-avatar__initials {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #fff;
  font-size: 2.4rem;
  font-weight: 800;
  text-transform: uppercase;
}
.pf-avatar__cam {
  position: absolute;
  inset: auto 0 0 0;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.55);
  opacity: 0;
  transition: opacity 0.2s ease;
}
.pf-avatar:hover .pf-avatar__cam,
.pf-avatar:focus-visible .pf-avatar__cam {
  opacity: 1;
}
.pf-id__name {
  margin: 4px 0 0;
  font-size: clamp(1.7rem, 3.4vw, 2.35rem);
  font-weight: 700;
  line-height: 1.05;
  color: var(--ink);
}
.pf-id__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}
.pf-stamp {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(
    135deg,
    var(--dk-pink, #e8276f),
    var(--dk-pink-deep, #b51f57)
  );
  box-shadow: 0 8px 18px -10px rgba(232, 39, 111, 0.8);
}
.pf-stamp .v-icon {
  color: #fff;
}
.pf-stamp--alert {
  background: linear-gradient(
    135deg,
    var(--dk-ember, #ff7a3d),
    var(--dk-ember, #e85526)
  );
  box-shadow: 0 8px 18px -10px rgba(232, 85, 38, 0.8);
}
.pf-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--ink);
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.pf-chip--soft {
  color: var(--muted);
}
.pf-chip .v-icon {
  color: var(--accent);
}
.pf-id__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

/* Botões */
.pf-btn.v-btn {
  background: var(--accent) !important;
  color: #fff !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}
.pf-btn-ghost.v-btn {
  color: var(--accent) !important;
  border-color: var(--accent) !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}
.pf-btn-ember.v-btn {
  background: var(--ember) !important;
  color: #fff !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 700;
}

/* ---------- Rail de abas ---------- */
.pf-rail {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 22px 0 16px;
  padding: 6px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 16px;
  box-shadow: 0 12px 30px -28px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pf-rail__tab {
  display: inline-flex;
  align-items: center;
  padding: 9px 16px;
  border-radius: 11px;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--muted);
  background: transparent;
  transition: background 0.18s ease, color 0.18s ease;
}
.pf-rail__tab .v-icon {
  color: currentColor;
}
.pf-rail__tab:hover {
  color: var(--ink);
  background: var(--paper);
}
.pf-rail__tab--on {
  color: #fff;
  background: var(--accent);
  box-shadow: 0 10px 20px -12px rgba(232, 39, 111, 0.7);
}
.pf-rail__tab--on:hover {
  color: #fff;
  background: var(--accent);
}

.pf-tabs-items {
  background: transparent !important;
  overflow: visible;
}

/* ---------- Cards / seções ---------- */
/* Convite de completar o perfil (card 86ak6hc2p). */
.pf-complete {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 18px;
  padding: 18px 20px;
  background: var(--dk-surface, #fff);
  border: 1px solid rgba(232, 39, 111, 0.28);
  border-radius: 18px;
}
.pf-complete__icon {
  margin-top: 2px;
}
.pf-complete__body {
  flex: 1 1 auto;
  min-width: 0;
}
.pf-complete__title {
  font-size: 0.98rem;
  font-weight: 800;
  color: var(--ink);
}
.pf-complete__text {
  margin: 6px 0 0;
  max-width: 68ch;
  font-size: 0.86rem;
  line-height: 1.55;
  color: var(--muted);
}
.pf-complete__btn {
  flex: 0 0 auto;
  align-self: center;
  padding: 10px 18px;
  border: none;
  border-radius: 12px;
  background: var(--dk-pink, #e8276f);
  color: #fff;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.pf-complete__btn:hover {
  filter: brightness(1.06);
}
@media (max-width: 700px) {
  .pf-complete {
    flex-wrap: wrap;
  }
  .pf-complete__btn {
    width: 100%;
  }
}

.pf-card {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
}
.pf-card--pad {
  padding: 24px 26px;
}
.pf-section-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink);
}
.pf-section-title--icon {
  display: flex;
  align-items: center;
}
.pf-section-title--icon .v-icon {
  color: var(--accent);
}
.pf-section-sub {
  margin-top: 4px;
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.45;
}
.pf-strong {
  font-weight: 700;
  color: var(--ink);
}
.pf-field-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 6px;
}
.pf-subhead {
  margin: 18px 0 10px;
}
.pf-inset {
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--paper);
}

/* ---------- Disponibilidade ---------- */
/* Aviso de quem chegou aqui vindo do match sem horario cadastrado. */
.pf-gate {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 93, 42, 0.08);
  border: 1px solid rgba(255, 93, 42, 0.25);
}
.pf-gate__icon {
  flex: 0 0 auto;
  margin-top: 1px;
}
.pf-gate__title {
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--dk-ember-deep, #b5421e);
}
.pf-gate__text {
  margin: 4px 0 0;
  max-width: 62ch;
  font-size: 0.84rem;
  line-height: 1.5;
  color: var(--dk-ember-deep, #7a3517);
}
.pf-gate__link {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 700;
  color: var(--dk-ember-deep, #b5421e);
  text-decoration: underline;
  cursor: pointer;
}
.pf-gate__link:focus-visible {
  outline: 2px solid var(--dk-ember-deep, #b5421e);
  outline-offset: 2px;
}

.pf-slot {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr auto;
  gap: 14px;
  align-items: end;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.pf-slot__del.v-btn {
  color: var(--ember) !important;
  margin-bottom: 2px;
}
.pf-add {
  display: inline-flex;
  align-items: center;
  margin-top: 18px;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--accent);
  background: var(--accent-soft);
  transition: background 0.18s ease;
}
.pf-add:hover {
  background: rgba(232, 39, 111, 0.18);
}
.pf-add .v-icon {
  color: var(--accent);
}

/* ---------- Planos ---------- */
.pf-plans {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.pf-plan {
  text-align: center;
  padding: 20px 16px;
  border-radius: 16px;
  border: 1px solid var(--line);
  background: var(--paper);
}
.pf-plan--on {
  border-color: var(--accent);
  background: var(--dk-surface, #fff);
  box-shadow: 0 16px 34px -24px rgba(232, 39, 111, 0.6);
}
.pf-plan__tier {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
.pf-plan--on .pf-plan__tier {
  color: var(--accent);
}
.pf-plan__num {
  font-size: 2.6rem;
  font-weight: 700;
  line-height: 1.05;
  color: var(--ink);
}
.pf-plan--on .pf-plan__num {
  color: var(--accent);
}
.pf-plan__unit {
  font-size: 0.78rem;
  color: var(--muted);
}
.pf-plan__price {
  margin-top: 8px;
  font-size: 1.02rem;
  font-weight: 800;
  color: var(--ink);
}
.pf-plan__btn.v-btn {
  margin-top: 12px;
}

/* ---------- Financeiro ---------- */
.pf-addcard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 180px;
  padding: 20px;
  border-radius: 16px;
  border: 1.5px dashed var(--line);
  background: var(--paper);
  color: var(--muted);
  font-size: 0.86rem;
  font-weight: 600;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.pf-addcard:hover {
  border-color: var(--accent);
  background: var(--dk-surface, #fff);
}

/* ---------- Responsivo ---------- */
@media (max-width: 900px) {
  .pf-card-id {
    grid-template-columns: auto 1fr;
  }
  .pf-id__actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
}
@media (max-width: 640px) {
  .pf-card-id {
    grid-template-columns: 1fr;
    justify-items: start;
    text-align: left;
  }
  .pf-slot {
    grid-template-columns: 1fr 1fr;
  }
  .pf-plans {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pf-avatar__cam,
  .pf-rail__tab,
  .pf-add,
  .pf-addcard {
    transition: none;
  }
}
</style>
